import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import styles from "./AppShell.module.css";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";

import { PRIMARY_NAV } from "@/app/navigation";
import { TopbarBackProvider } from "@/app/providers/TopbarBackProvider";
import { SettingsIcon } from "@/components/icons";
import { ROUTES } from "@/config/routes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { NavItem as NavItemType } from "@/types/navigation";

/**
 * The persistent application frame: sidebar + topbar around the routed page
 * (rendered into <Outlet />). Owns the sidebar collapsed state. The page title
 * is derived from the active nav item so it stays in sync with the route.
 */
export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { permissions } = useCurrentUser();

  // Filter nav items by permission. Items with no `requires` are always visible.
  const visibleNav = PRIMARY_NAV.filter(
    (item) => !item.requires || permissions.includes(item.requires)
  );

  // Settings slot: live link for rbac.manage holders; null shows the placeholder.
  const settingsItem: NavItemType | null = permissions.includes("rbac.manage")
    ? { id: "settings", label: "Settings", path: ROUTES.settingsAccess, icon: SettingsIcon }
    : null;

  // Match the most specific nav item: an exact path, or the longest nav path
  // the current route sits under (so sub-routes like /tools/x keep the section).
  // Title derives from the FULL nav list (not visibleNav) plus the settings item
  // so /settings/access resolves to "Settings" rather than falling back to "IOG OS".
  const allNavForTitle: NavItemType[] = [...PRIMARY_NAV, ...(settingsItem ? [settingsItem] : [])];
  const activeItem = allNavForTitle
    .filter(
      (item) =>
        location.pathname === item.path ||
        (item.path !== "/" && location.pathname.startsWith(`${item.path}/`))
    )
    .sort((a, b) => b.path.length - a.path.length)[0];
  const title = activeItem?.label ?? "IOG OS";

  return (
    <TopbarBackProvider>
      <div className={collapsed ? `${styles.app} ${styles.appCollapsed}` : styles.app}>
        <Sidebar
          items={visibleNav}
          activePath={location.pathname}
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((value) => !value)}
          settingsItem={settingsItem}
        />
        <main className={styles.main} id="main" tabIndex={-1}>
          <Topbar title={title} />
          <section className={styles.content} aria-label="Dashboard content">
            <Outlet />
          </section>
        </main>
      </div>
    </TopbarBackProvider>
  );
}
